import { toTitleCase } from "./ActivitiesTableUtilFunctions";

export const COLUMN_ORDER = (rows: Record<string, unknown>[]): string[] => {
  if (rows.length === 0) return [];
  return [
    "subject",
    "study_status",
    "subject_group",
    "sex",
    "birth_date",
    "race",
    "ethnic",
    "indication",
    "site",
    "country",
    "start_date",
    "enrollment_date_derived",
    "participant_age_at_screening",
    "participant_age_at_screening_unit",
    "consent_signed_date_derived",
    "protocol_amendment_version",
    "is_assent",
    "assent_signed_date_derived",
  ].filter((column) => rows[0][column] !== undefined);
};

export const COLUMN_WIDTHS: Record<string, number> = {
  // Medical review. Role slugs are joined with commas when someone holds more
  // than one, and the 130 default wraps them onto a second line. Only names
  // unique to medical review are set here: this map is shared with every
  // dataset screen, so widening a column another dataset also has would
  // silently resize a table nobody asked us to touch.
  performed_by_role: 240,
  action_label: 150,
  assigned_to_name: 160,
  reviewed_by: 160,
  subject: 110,
  site: 100,
  country: 150,
  randomization_status: 165,
  randomization_date: 150,
  randomization_time: 150,
  screen_status: 150,
  indication: 150,
  indication_subtype: 240,
  study_status: 200,
  subject_group: 220,
  ethnic: 200,
  start_date: 200,
  protocol_amendment_version: 230,
  participant_age_at_screening_unit: 200,
  consent_signed_date_derived: 200,
  assent_signed_date_derived: 200,
  // One unbreakable 13-character word plus the sort affordance: it cannot
  // wrap at a space the way 'Screen Status' (150) does.
  discrepancies: 170,
  DEFAULT: 130,
};

export const DEFAULT_HIDDEN_COLUMNS = new Set([
  "indication",
  "participant_id",
  "childber",
  "indication_long",
  "randomization_date",
  "randomization_time",
  "start_date",
  "completion_date",
  "last_dose_date",
  "discont_reason",
]);

export const FIXED_COLUMNS = new Set([
  "subject",
  "study_arm_label",
  "participant_id",
  "site",
  "country",
]);

export const STATUS_COLUMNS = new Set([
  "screen_status",
  "randomization_status",
  "study_status",
  "study_status_raw",
]);

export const PILL_APPLIED_COLUMNS = new Set([
  "country",
  "yob",
  "current_age",
  "sex",
  "childber",
  "race",
  "ethnic",
  "indication",
  "indication_long",
  "screen_status",
  "randomization_status",
  "study_status",
  "randomization_date",
  "randomization_time",
  "start_date",
  "completion_date",
  "last_dose_date",
  "discont_reason",
]);

export const SEARCHABLE_COLUMNS = new Set([
  "subject",
  "site",
  "participant_id",
  "yob",
  "age",
]);

export const NUMERIC_COLUMNS = new Set(["participant_id", "site"]);

export const COLUMNS_WITH_LINK = new Set(["subject"]);

export const TITLE_MAPPING: Record<string, string> = {
  participant_id: "Participant ID",
  subject: "Subject ID",
  site: "Site ID",
  yob: "Birth Year",
  day_of_birth: "Birth Day",
  month_of_birth: "Birth Month",
  birth_date: "DOB",
  participant_age_at_screening: "Age at Screen",
  participant_age_at_screening_unit: "Age at Screen unit",
  current_age: "Current Age",
  ethnic: "Ethnicity",
  indication: "Indication Name",
  indication_long: "Indication Long Name",
  symptom_start_date_derived: "Indication Symptom Start Date",
  diagnosis_date_derived: "Indication Diagnosis Date",
  test_name: "Indication Test Name",
  test_latest_date_derived: "Indication Latest Test Date",
  test_latest_score: "Indication Latest Test Score",
  test_done_prescreening: "Indication Test Done Prescreening",
  study_arm_label: "Study Arm",
  start_date: "Informed Consent Date",
  consent_signed_date_derived: "Consent Signed Date",
  study_identifier: "Prior Study",
  prior_subject_identifier: "Prior Subject ID",
  enrollment_date_derived: "Enrollment Date",
  referral_type_source_value: "Referral Type",
  childber: "Childbearing Potential",
  screening_date_derived: "Screening Date",
  all_ie_criteria_passed: "All IE Criteria Passed",
  screening_failure_date_derived: "Screening Failure Date",
  screen_status: "Screening Pass",
  failure_reason_source_value: "Screening Failure Reason",
  enrollment_confirmed_by_medical_monitoring: "Enrollment Confirmed",
  eos_status_source_value: "End Of Study Status",
  completion_date: "Study End Date",
  discont_reason: "Discontinuation Reason",
  discontinuation_reason_ae_source_value: "Discontinuation Reason AE",
  cause_source_value: "Death Cause",
  death_date_derived: "Death Date",
  cause_source_value_ae: "Death Cause AE",
  assent_signed_date_derived: "Assent Signed Date",
  assent_sent_date_derived: "Assent Sent Date",
  medical_monitoring_approval_date_derived: "Medical Monitoring Approval Date",
};

export const INDEX_COLUMNS = new Set(["subject", "participant_id"]);

export const defaultColumns: Record<string, string[]> = {
  PROPEL: [
    "subject",
    "study_status",
    "subject_group",
    "sex",
    "birth_date",
    "race",
    "ethnic",
    "indication",
    "site",
    "country",
    "start_date",
    "enrollment_date_derived",
    "participant_age_at_screening",
    "participant_age_at_screening_unit",
    "consent_signed_date_derived",
    "protocol_amendment_version",
    "is_assent",
    "assent_signed_date_derived",
  ],
  DEMO_TRIAL: [
    "subject",
    "study_status",
    "study_arm_label",
    "site",
    "country",
    "yob",
    "participant_age_at_screening",
    "sex",
    "race",
    "ethnic",
    "indication",
    "screen_status",
    "randomization_status",
  ],
};

export const hiddenColumns: Record<string, string[]> = {
  PROPEL: [
    "participant_id",
    "indication_long",
    "trial_nct_id",
    "vivo_received_date",
    "symptom_start_date_derived",
    "diagnosis_date_derived",
    "indication_stage_at_diagnosis",
    "test_name",
    "test_latest_date_derived",
    "test_latest_score",
    "test_done_prescreening",
    "study_arm_label",
    "consent_version",
    "referral_type_source_value",
    "screening_number",
    "screening_failure_date_derived",
    "failure_reason_source_value",
    "medical_monitoring_approval_date_derived",
    "ready_for_medical_monitoring_approval",
    "enrollment_confirmed_by_medical_monitoring",
    "sponsor_approve_enrollment",
    "randomization_number",
    "randomization_status",
    "randomization_date",
    "randomization_time",
    "wish_to_randomize",
    "eligible_to_randomize",
    "eos_status_source_value",
    "completion_date",
    "last_dose_date",
    "discont_reason",
    "discontinuation_reason_specify",
    "discontinuation_reason_ae_source_value",
    "safety_followup_done",
  ],
  DEMO_TRIAL: [
    "participant_id",
    "indication_long",
    "trial_nct_id",
    "vivo_received_date",
    "day_of_birth",
    "month_of_birth",
    "birth_date",
    "current_age",
    "symptom_start_date_derived",
    "is_assent",
    "is_reassent",
    "assent_sent_date_derived",
    "assent_signed_date_derived",
    "study_identifier",
    "subject_group",
    "prior_subject_identifier",
    "enrollment_date_derived",
    "participant_age_at_screening_unit",
    "screening_failure_date_derived",
    "failure_reason_source_value",
    "medical_monitoring_approval_date_derived",
    "ready_for_medical_monitoring_approval",
    "enrollment_confirmed_by_medical_monitoring",
    "sponsor_approve_enrollment",
    "eos_status_source_value",
    "completion_date",
    "last_dose_date",
    "discont_reason",
    "discontinuation_reason_specify",
    "discontinuation_reason_ae_source_value",
    "safety_followup_done",
    "death_time",
    "cause_source_value",
    "death_date_derived",
    "cause_source_value_ae",
    "was_autopsy_done",
    "autopsy_report_available",
  ],
};

const hardsetTitles: Record<string, string> = {
  participant_id: "Participant ID",
  subject: "Subject ID",
  site: "Site ID",
  yob: "Birth Year",
  day_of_birth: "Birth Day",
  month_of_birth: "Birth Month",
  birth_date: "DOB",
  current_age: "Current Age",
  ethnic: "Ethnicity",
  indication: "Indication Name",
  indication_long: "Indication Long Name",
  symptom_start_date_derived: "Indication Symptom Start Date",
  diagnosis_date_derived: "Indication Diagnosis Date",
  test_name: "Indication Test Name",
  test_latest_date_derived: "Indication Latest Test Date",
  test_latest_score: "Indication Latest Test Score",
  test_done_prescreening: "Indication Test Done Prescreening",
  study_arm_label: "Study Arm",
  start_date: "Informed Consent Date",
  consent_signed_date_derived: "Consent Signed Date",
  study_identifier: "Prior Study",
  prior_subject_identifier: "Prior Subject ID",
  enrollment_date_derived: "Enrollment Date",
  referral_type_source_value: "Referral Type",
  childber: "Childbearing Potential",
  screening_date_derived: "Screening Date",
  all_ie_criteria_passed: "All IE Criteria Passed",
  screening_failure_date_derived: "Screening Failure Date",
  screen_status: "Screening Pass",
  failure_reason_source_value: "Screening Failure Reason",
  enrollment_confirmed_by_medical_monitoring: "Enrollment Confirmed",
  eos_status_source_value: "End Of Study Status",
  completion_date: "Study End Date",
  discont_reason: "Discontinuation Reason",
  discontinuation_reason_ae_source_value: "Discontinuation Reason AE",
  cause_source_value: "Death Cause",
  death_date_derived: "Death Date",
  cause_source_value_ae: "Death Cause AE",
  participant_age_at_screening: "Age at Screen",
  participant_age_at_screening_unit: "Age at Screen unit",
  assent_signed_date_derived: "Assent Signed Date",
  assent_sent_date_derived: "Assent Sent Date",
  medical_monitoring_approval_date_derived: "Medical Monitoring Approval Date",
};

export const columnsTitleCreator = (column: string): string => {
  if (!column) return "";
  if (hardsetTitles[column.toLowerCase()])
    return hardsetTitles[column.toLowerCase()];
  return toTitleCase(column.replace("_", " "));
};
